import { Box, Typography, useTheme } from '@mui/material';

/**
 * The i2x wordmark with the hosting badges, as the VF dashboard shows them.
 *
 * Carried over rather than reinvented: the mark is the same company's, and two
 * hand-drawn approximations of one logo across two products is how a brand stops
 * being recognisable. The path data and the badge geometry come from
 * i2x-vf-dashboard (`src/components/common/Logo/i2xLogo.tsx` and the header's
 * flag styles).
 *
 * The German and EU flags travel together on purpose — in the dashboard they are
 * one badge cluster standing for "made and hosted in Germany", and showing one
 * without the other reads as a rendering fault rather than as a choice.
 */

const i2xBlue = '#4550E6';

function I2XLogo({ color = i2xBlue }: { color?: string }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 914 526"
      sx={{ display: 'block', width: '60px', height: '35px', flexShrink: 0 }}
      role="img"
      aria-label="i2x"
    >
      <path
        fill={color}
        fillRule="evenodd"
        d="M 67.027 124.13C 106.634 124.13 134.053 97.7521 134.053 62.0648C 134.053 26.3775 106.634 -9.47035e-05 67.027 -9.47035e-05C 27.4204 -9.47035e-05 0 26.3775 0 62.0648C 0 97.7521 27.4204 124.13 67.027 124.13ZM 7.61667 526L 127.96 526L 127.96 169.127L 7.61667 169.127L 7.61667 526ZM 193.464 526L 514.886 526L 514.886 423.593L 440.244 423.593C 420.44 423.593 388.45 426.696 365.6 429.799C 425.01 367.735 493.56 287.05 493.56 214.124C 493.56 124.13 431.104 65.1682 341.227 65.1682C 275.724 65.1682 233.07 88.4423 187.37 138.094L 251.35 203.263C 272.677 181.54 294.003 159.817 324.47 159.817C 356.46 159.817 379.31 178.437 379.31 220.33C 379.31 282.395 298.574 356.873 193.464 456.177L 193.464 526ZM 554.494 526L 659.603 339.805L 560.586 169.127L 688.547 169.127L 714.444 218.779C 723.584 238.95 732.724 260.673 741.864 280.844L 744.91 280.844C 751.004 260.673 760.143 238.95 766.236 218.779L 782.994 169.127L 907.906 169.127L 808.89 355.321L 914 526L 786.04 526L 758.62 476.348C 747.956 456.177 738.816 434.454 728.153 414.283L 725.106 414.283C 717.49 434.454 708.35 454.625 700.733 476.348L 680.93 526L 554.494 526ZM 565.156 1.55162L 898.767 1.55162L 898.767 94.649L 565.156 94.649L 565.156 1.55162Z"
      />
    </Box>
  );
}

function GermanFlag() {
  return (
    <Box
      role="img"
      aria-label="Made and hosted in Germany"
      title="Made & hosted in Germany"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 6,
        height: 20,
        borderRadius: '2px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        '& > div': { height: '33.33%', width: '100%' }
      }}
    >
      <div style={{ backgroundColor: '#000000' }} />
      <div style={{ backgroundColor: '#DD0000' }} />
      <div style={{ backgroundColor: '#FFCE00' }} />
    </Box>
  );
}

function EUFlag() {
  return (
    <Box
      sx={{
        p: '2px',
        width: 20,
        height: 20,
        borderRadius: '2px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        fill: '#FFCE00',
        backgroundColor: '#003399',
        lineHeight: 0
      }}
      role="img"
      aria-label="Hosted in the European Union"
      title="Hosted in the EU"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 512 512" style={{ display: 'block' }}>
        <path d="m256 46.305-9.404 19.054-21.03 3.056 15.217 14.832-3.592 20.945L256 94.305l18.81 9.888-3.593-20.945 15.217-14.832-21.03-3.057L256 46.304zM167.566 72.63l-9.404 19.056-21.03 3.056 15.218 14.832-3.592 20.946 18.808-9.89 18.81 9.89-3.593-20.946L198 94.742l-21.03-3.056-9.404-19.055zm176.868 0-9.405 19.056L314 94.742l15.217 14.832-3.592 20.946 18.81-9.89 18.807 9.89-3.592-20.946 15.217-14.832-21.03-3.056-9.403-19.055zm-243.868 67.425-9.404 19.054-21.03 3.056 15.218 14.832-3.592 20.945 18.808-9.888 18.81 9.888-3.593-20.945L131 162.166l-21.03-3.057-9.404-19.055zm310.868 0-9.405 19.054-21.03 3.056 15.217 14.832-3.592 20.945 18.81-9.888 18.807 9.888-3.592-20.945 15.217-14.832-21.03-3.057-9.403-19.055zM76.566 228.55l-9.404 19.054-21.03 3.056 15.218 14.832-3.592 20.945 18.808-9.888 18.81 9.887-3.593-20.945L107 250.66l-21.03-3.056-9.404-19.055zm358.868 0-9.405 19.054L405 250.66l15.217 14.832-3.592 20.945 18.81-9.888 18.807 9.887-3.592-20.945 15.217-14.832-21.03-3.056-9.403-19.055zm-334.868 89.897-9.404 19.055-21.03 3.057 15.218 14.83-3.592 20.946 18.808-9.89 18.81 9.89-3.593-20.945L131 340.56l-21.03-3.058zm310.868 0-9.405 19.055L381 340.56l15.217 14.83-3.592 20.946 18.81-9.89 18.807 9.89-3.592-20.945 15.217-14.83-21.03-3.058-9.403-19.055zm-243.868 65.746-9.404 19.055-21.03 3.057 15.218 14.832-3.592 20.945 18.808-9.89 18.81 9.89-3.593-20.945L198 406.305l-21.03-3.057zm176.868 0-9.405 19.055-21.03 3.057 15.217 14.832-3.592 20.945 18.81-9.89 18.807 9.89-3.592-20.945 15.217-14.832-21.03-3.057-9.403-19.055zm-88.61 23.614-9.404 19.056-21.03 3.055 15.217 14.834-3.59 20.943.385-.203-.035.203L256 455.898l18.633 9.797-.035-.203.386.203-3.59-20.943 15.215-14.834-21.03-3.055-9.404-19.056-.176.355-.176-.355z" />
      </svg>
    </Box>
  );
}

export function Brand() {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <I2XLogo color={theme.palette.mode === 'dark' ? theme.palette.common.white : i2xBlue} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 0.5,
          position: 'relative',
          pr: '20px',
          zIndex: 3
        }}
      >
        <GermanFlag />
        <EUFlag />
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ alignSelf: 'center', display: { xs: 'none', sm: 'block' } }}
      >
        Archiv · Dokumentensuche
      </Typography>
    </Box>
  );
}
